<template>
  <div class="content">
    <div class="popup-form-shell">
      <form class="form-horizontal" @submit.prevent="onSubmit">
        <card class="text-left popup-form-card">
          <template slot="header">
            <div class="popup-form-header">
              <div>
                <span v-if="popupId" class="popup-form-id">ID {{ popupId }}</span>
                <h4 class="card-title">{{ popupId ? "팝업 상세" : "팝업 등록" }}</h4>
              </div>
              <div class="popup-form-statuses">
                <span class="badge badge-pill badge-info">{{ popupTypeLabel }}</span>
                <span class="badge badge-pill" :class="detailState.badgeClass">{{ detailState.label }}</span>
              </div>
            </div>
          </template>

          <section class="popup-form-section popup-image-section">
            <h5 class="popup-section-title">이미지</h5>
            <div class="row popup-image-row">
              <div class="col-lg-4 col-md-6 popup-image-field">
                <label>한국어 이미지 <span class="popup-required-mark" aria-hidden="true">*</span></label>
                <div class="popup-image-upload">
                  <BlipFileUploadZone :upload-category="6" :maxFiles="1" :fileUrl="newImageFileId | blipImageUrl" @onChangeFileIds="onNewFileIdChange" />
                </div>
              </div>
              <div class="col-lg-4 col-md-6 popup-image-field">
                <label>영어 이미지 <span class="popup-required-mark" aria-hidden="true">*</span></label>
                <div class="popup-image-upload">
                  <BlipFileUploadZone :upload-category="6" :maxFiles="1" :fileUrl="newImageFileEnId | blipImageUrl" @onChangeFileIds="onNewFileIdEnChange" />
                </div>
              </div>
              <div class="col-lg-4 col-md-6 popup-image-field">
                <label>일본어 이미지 <span class="popup-required-mark" aria-hidden="true">*</span></label>
                <div class="popup-image-upload">
                  <BlipFileUploadZone :upload-category="6" :maxFiles="1" :fileUrl="newImageFileJaId | blipImageUrl" @onChangeFileIds="onNewFileIdJaChange" />
                </div>
              </div>
            </div>
          </section>

          <section class="popup-form-section">
            <h5 class="popup-section-title">기본 정보</h5>
            <div class="popup-form-grid">
              <div class="popup-form-field popup-form-field-wide">
                <label>이름 <span class="popup-required-mark" aria-hidden="true">*</span></label>
                <base-input placeholder="팝업 이름" v-model="name">
                  <span slot="helpBlock" class="form-text" />
                </base-input>
              </div>

              <div class="popup-form-field">
                <label>팝업 유형 <span class="popup-required-mark" aria-hidden="true">*</span></label>
                <div class="checkbox-radios popup-inline-options">
                  <base-radio name="bottomPopup" inline v-model="popupType">하단 팝업</base-radio>
                  <base-radio name="imageModal" inline v-model="popupType">이미지 모달</base-radio>
                </div>
              </div>
            </div>
          </section>

          <section class="popup-form-section">
            <h5 class="popup-section-title">이동 링크</h5>
            <div class="popup-form-grid">
              <div class="popup-form-field">
                <label>링크 구분</label>
                <div class="checkbox-radios popup-inline-options">
                  <base-radio name="INTERNAL" inline v-model="linkType">내부</base-radio>
                  <base-radio name="EXTERNAL" inline v-model="linkType">외부</base-radio>
                </div>
              </div>

              <div class="popup-form-field">
                <label>링크 옵션</label>
                <LinkSelect @linkChangeEvent="getLink" class="link-selection" link-model="link" :allowed-link-type="linkType" allow-custom platform-aware />
              </div>

              <div class="popup-form-field popup-form-field-wide">
                <label class="platform-link-label">
                  <span>앱 링크</span>
                  <span class="badge badge-pill" :class="hasAppLink ? 'badge-success' : 'badge-secondary'">
                    {{ hasAppLink ? "노출" : "미노출" }}
                  </span>
                </label>
                <base-input placeholder="딥링크 또는 HTTPS · 비우면 웹 전용" v-model="link">
                  <span slot="helpBlock" class="form-text" />
                </base-input>
              </div>

              <div class="popup-form-field popup-form-field-wide">
                <label class="platform-link-label">
                  <span>웹 링크</span>
                  <span class="badge badge-pill" :class="hasWebLink ? 'badge-success' : 'badge-secondary'">
                    {{ hasWebLink ? "노출" : "미노출" }}
                  </span>
                </label>
                <base-input placeholder="HTTP 또는 HTTPS · 비우면 앱 전용" v-model="webLink">
                  <span slot="helpBlock" class="form-text" />
                </base-input>
              </div>
            </div>
          </section>

          <section class="popup-form-section">
            <h5 class="popup-section-title">노출 기간</h5>
            <div class="popup-form-grid">
              <div class="popup-form-field">
                <label>노출 시작 시간 <span class="popup-required-mark" aria-hidden="true">*</span></label>
                <base-input placeholder="시작시간">
                  <el-date-picker type="datetime" placeholder="시작시간" v-model="startTime"></el-date-picker>
                </base-input>
              </div>
              <div class="popup-form-field">
                <label>노출 종료 시간 <span class="popup-required-mark" aria-hidden="true">*</span></label>
                <base-input placeholder="종료시간">
                  <el-date-picker type="datetime" placeholder="종료시간" v-model="endTime"></el-date-picker>
                </base-input>
              </div>
            </div>
          </section>

          <section class="popup-form-section popup-target-section">
            <h5 class="popup-section-title">노출 대상</h5>
            <div class="popup-target-grid">
              <div class="popup-target-selector">
                <div class="popup-target-heading">
                  <div>
                    <h6>노출 국가</h6>
                    <span class="popup-target-summary">{{ countryTargetSummary }}</span>
                  </div>
                  <div class="popup-target-mode">
                    <base-radio name="all" inline v-model="countryTargetMode">전체</base-radio>
                    <base-radio name="selected" inline v-model="countryTargetMode">직접 선택</base-radio>
                  </div>
                </div>
                <el-select
                  v-if="countryTargetMode === 'selected'"
                  v-model="selectedCountryCodes"
                  class="popup-target-select"
                  placeholder="국가 검색 및 선택"
                  multiple
                  filterable
                  clearable
                  collapse-tags
                >
                  <el-option
                    v-for="country in countryCodes"
                    :key="country.isoCode || country.code"
                    :label="country.name"
                    :value="country.isoCode || country.code"
                  />
                </el-select>
                <div v-if="countryTargetMode === 'selected'" class="popup-target-selected">
                  <div class="popup-target-selected-heading">
                    <span>선택된 국가</span>
                    <span>{{ selectedCountries.length }}개</span>
                  </div>
                  <div v-if="selectedCountries.length" class="popup-target-tags">
                    <el-tag v-for="country in selectedCountries" :key="country.value" closable size="small" @close="removeCountry(country.value)">
                      {{ country.label }}
                    </el-tag>
                  </div>
                  <div v-else class="popup-target-empty">국가를 검색해 노출 대상을 선택해 주세요.</div>
                </div>
                <div v-else class="popup-target-all"><i class="tim-icons icon-check-2"></i><span>전체 국가</span></div>
              </div>

              <div class="popup-target-selector">
                <div class="popup-target-heading">
                  <div>
                    <h6>노출 아티스트</h6>
                    <span class="popup-target-summary">{{ unitTargetSummary }}</span>
                  </div>
                  <div class="popup-target-mode">
                    <base-radio name="all" inline v-model="unitTargetMode">전체</base-radio>
                    <base-radio name="selected" inline v-model="unitTargetMode">직접 선택</base-radio>
                  </div>
                </div>
                <el-select
                  v-if="unitTargetMode === 'selected'"
                  v-model="selectedUnitIds"
                  class="popup-target-select"
                  placeholder="아티스트 검색 및 선택"
                  multiple
                  filterable
                  clearable
                  collapse-tags
                >
                  <el-option v-for="unit in units" :key="unit.unitId" :label="unit.blipName || unit.name" :value="unit.unitId" />
                </el-select>
                <div v-if="unitTargetMode === 'selected'" class="popup-target-selected">
                  <div class="popup-target-selected-heading">
                    <span>선택된 아티스트</span>
                    <span>{{ selectedUnits.length }}개</span>
                  </div>
                  <div v-if="selectedUnits.length" class="popup-target-tags">
                    <el-tag v-for="unit in selectedUnits" :key="unit.value" closable size="small" @close="removeUnit(unit.value)">
                      {{ unit.label }}
                    </el-tag>
                  </div>
                  <div v-else class="popup-target-empty">아티스트를 검색해 노출 대상을 선택해 주세요.</div>
                </div>
                <div v-else class="popup-target-all"><i class="tim-icons icon-check-2"></i><span>전체 아티스트</span></div>
              </div>
            </div>
          </section>
        </card>

        <div class="popup-form-actions">
          <base-button native-type="submit" type="success">{{ popupId ? "수정" : "등록" }}</base-button>
          <base-button @click="goBack" type="default">취소</base-button>
          <base-button v-if="popupId && state === 1" @click="setPopupState(0)" type="default">숨김</base-button>
          <base-button v-if="popupId && state === 0" @click="setPopupState(1)" type="default">표시</base-button>
          <base-button v-if="popupId" @click="setPopupState(-1)" type="danger">삭제</base-button>
        </div>
      </form>
    </div>
  </div>
</template>
<script>
import { BaseInput, BaseRadio, Card } from "@/components/index";
import BlipFileUploadZone from "@/components/BlipFileUploadZone";
import { Toast } from "@/util/Toast";
import LinkSelect from "@/pages/Components/LinkSelect.vue";

export default {
  components: {
    Card,
    BaseInput,
    BaseRadio,
    LinkSelect,
    BlipFileUploadZone,
  },
  async created() {
    await this.getAllUnits();
    try {
      await this.getCountryCodes();
    } catch (e) {
      console.error(e);
    }
    if (this.$route.query.popupId) {
      this.popupId = Number(this.$route.query.popupId);
      await this.getData();
    }
  },
  data() {
    return {
      popupId: null,
      name: null,
      imageFileId: null,
      imageFileEnId: null,
      imageFileJaId: null,
      newImageFileId: null,
      newImageFileEnId: null,
      newImageFileJaId: null,
      link: null,
      webLink: null,
      linkType: "INTERNAL",
      popupType: "imageModal",
      startTime: null,
      endTime: null,
      countryCodes: [],
      countryTargetMode: "all",
      selectedCountryCodes: [],
      state: 1,
      units: [],
      unitTargetMode: "all",
      selectedUnitIds: [],
    };
  },
  computed: {
    hasAppLink() {
      return Boolean(this.link && this.link.trim());
    },
    hasWebLink() {
      return Boolean(this.webLink && this.webLink.trim());
    },
    popupTypeLabel() {
      return this.popupType === "bottomPopup" ? "하단 팝업" : "이미지 모달";
    },
    detailState() {
      if (!this.popupId) {
        return { label: "신규", badgeClass: "badge-light" };
      }
      if (this.state !== 1) {
        return { label: this.state === -1 ? "삭제" : "숨김", badgeClass: "badge-secondary" };
      }
      const now = Date.now();
      const startsAt = new Date(this.startTime).getTime();
      const endsAt = new Date(this.endTime).getTime();
      if (Number.isFinite(startsAt) && Number.isFinite(endsAt) && (now < startsAt || now > endsAt)) {
        return { label: "기간 외", badgeClass: "badge-warning" };
      }
      return { label: "표시", badgeClass: "badge-success" };
    },
    countryTargetSummary() {
      return this.countryTargetMode === "all" ? "전체 국가" : `${this.selectedCountryCodes.length}개 선택`;
    },
    unitTargetSummary() {
      return this.unitTargetMode === "all" ? "전체 아티스트" : `${this.selectedUnitIds.length}개 선택`;
    },
    selectedCountries() {
      return this.selectedCountryCodes.map((code) => {
        const country = this.countryCodes.find((item) => (item.isoCode || item.code) === code);
        return { value: code, label: country ? country.name : code };
      });
    },
    selectedUnits() {
      return this.selectedUnitIds.map((unitId) => {
        const unit = this.units.find((item) => item.unitId === unitId);
        return { value: unitId, label: unit ? unit.blipName || unit.name : String(unitId) };
      });
    },
  },
  methods: {
    removeCountry(countryCode) {
      this.selectedCountryCodes = this.selectedCountryCodes.filter((code) => code !== countryCode);
    },
    removeUnit(unitId) {
      this.selectedUnitIds = this.selectedUnitIds.filter((id) => id !== unitId);
    },
    onNewFileIdChange(newFileIds) {
      this.newImageFileId = newFileIds[0];
    },
    onNewFileIdEnChange(newFileIds) {
      this.newImageFileEnId = newFileIds[0];
    },
    onNewFileIdJaChange(newFileIds) {
      this.newImageFileJaId = newFileIds[0];
    },
    async getAllUnits() {
      return this.$axios.get(`/artistUnit`).then((res) => {
        this.units = res.data.data || [];
      });
    },
    async getCountryCodes() {
      return this.$axios.get(`/common/countryCodes`).then((res) => {
        this.countryCodes = res.data.data || [];
      });
    },
    async getData() {
      return this.$axios.get(`/popup/${this.popupId}`).then((res) => {
        const data = res.data.data;
        if (data) {
          this.name = data.name;
          this.imageFileId = data.imageFileId;
          this.imageFileEnId = data.imageFileEnId;
          this.imageFileJaId = data.imageFileJaId;
          this.link = data.link;
          this.webLink = data.webLink;
          this.linkType = data.linkType || this.inferLinkType(data.link);
          this.popupType = data.isAd ? "bottomPopup" : "imageModal";
          this.startTime = data.startTime;
          this.endTime = data.endTime;
          this.state = data.state;
          const images = this.parseJsonSafely(data.images) || [];
          this.newImageFileId = images.find((image) => image.code === "ko")?.imageFileId;
          this.newImageFileEnId = images.find((image) => image.code === "en")?.imageFileId;
          this.newImageFileJaId = images.find((image) => image.code === "ja")?.imageFileId;

          this.selectedCountryCodes = data.countryCodes ? data.countryCodes.split(",").map((code) => code.trim()) : [];
          this.countryTargetMode = this.selectedCountryCodes.length > 0 ? "selected" : "all";
          this.selectedUnitIds = data.unitIds ? data.unitIds.split(",").map(Number) : [];
          this.unitTargetMode = this.selectedUnitIds.length > 0 ? "selected" : "all";
        }
      });
    },
    async setPopupState(state) {
      return this.$axios
        .put(`/popup/${this.popupId}/state`, {
          params: { state },
        })
        .then(() => {
          Toast.success("상태가 변경되었습니다");
          this.state = state;
        });
    },
    getLink(param) {
      if (param.custom) {
        return;
      }
      const linkModel = param.linkModel.split(".");
      this[linkModel[0]] = param.value;
      this.webLink = param.webValue;
    },
    inferLinkType(link) {
      return /^(https?:\/\/|blip(dev)?:\/\/external(?:[/?]|$))/i.test(link || "") ? "EXTERNAL" : "INTERNAL";
    },
    isHttpUrl(link) {
      return /^https?:\/\//i.test(link || "");
    },
    parseJsonSafely(input) {
      if (!input || typeof input !== "string") {
        return input;
      }
      try {
        return JSON.parse(input);
      } catch (e) {
        return null;
      }
    },
    async onSubmit() {
      if (!this.name || !this.name.trim()) {
        Toast.error("이름을 입력해주세요");
        return;
      }
      if (!this.newImageFileId || !this.newImageFileEnId || !this.newImageFileJaId) {
        Toast.error("이미지는 필수입니다");
        return;
      }
      if (!this.startTime || !this.endTime) {
        Toast.error("시간을 입력해주세요");
        return;
      }
      if (new Date(this.startTime) > new Date(this.endTime)) {
        Toast.error("종료시간이 시작시간보다 빨라요");
        return;
      }
      if (this.webLink && !this.isHttpUrl(this.webLink)) {
        Toast.error("웹 링크는 HTTP 또는 HTTPS 링크를 입력해주세요");
        return;
      }
      if (this.countryTargetMode === "selected" && this.selectedCountryCodes.length === 0) {
        Toast.error("노출 국가를 하나 이상 선택해주세요");
        return;
      }
      if (this.unitTargetMode === "selected" && this.selectedUnitIds.length === 0) {
        Toast.error("노출 아티스트를 하나 이상 선택해주세요");
        return;
      }

      const countryCodes = this.countryTargetMode === "selected" ? this.selectedCountryCodes : [];
      const units = this.unitTargetMode === "selected" ? this.selectedUnitIds : [];
      const params = {
        name: this.name,
        imageFileId: this.imageFileId,
        imageFileEnId: this.imageFileEnId,
        imageFileJaId: this.imageFileJaId,
        images: [
          { code: "ko", imageFileId: this.newImageFileId },
          { code: "en", imageFileId: this.newImageFileEnId },
          { code: "ja", imageFileId: this.newImageFileJaId },
        ],
        link: this.link || null,
        webLink: this.webLink || null,
        linkType: this.linkType,
        isAd: this.popupType === "bottomPopup",
        startTime: this.startTime,
        endTime: this.endTime,
        state: this.state,
        countryCodes: countryCodes.length > 0 ? countryCodes.toString() : null,
        unitIds: units.length > 0 ? units.toString() : null,
      };
      try {
        if (this.popupId) {
          await this.$axios.put(`/popup/${this.popupId}`, params);
          Toast.success("팝업이 수정되었습니다");
        } else {
          await this.$axios.post("/popup", params);
          Toast.success("팝업이 등록되었습니다");
        }
        this.$router.push(`/banner/popup`);
      } catch (e) {
        console.log(e);
        Toast.error(`처리중 에러가 발생하였습니다`);
      }
    },
    goBack() {
      history.go(-1);
    },
  },
};
</script>
<style scoped lang="scss">
.popup-form-shell {
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
}

.popup-form-card {
  overflow: hidden;
}

.popup-form-header {
  display: flex;
  gap: 24px;
  align-items: flex-start;
  justify-content: space-between;
}

.popup-form-id {
  display: block;
  margin-bottom: 3px;
  color: #8898aa;
  font-size: 0.72rem;
  font-weight: 600;
}

.popup-form-header .card-title {
  margin: 0;
}

.popup-form-statuses {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.badge-light {
  border: 1px solid #d9dde7;
  background: #fff;
  color: #525f7f;
}

.popup-form-section {
  padding: 28px 0;
  border-top: 1px solid rgba(34, 42, 66, 0.12);
}

.popup-form-section:first-child {
  padding-top: 8px;
  border-top: 0;
}

.popup-section-title {
  margin: 0 0 18px;
  color: #32325d;
  font-size: 1rem;
  font-weight: 700;
}

.popup-image-row {
  row-gap: 24px;
}

.popup-image-field label,
.popup-form-field > label {
  display: block;
  margin-bottom: 8px;
  color: #525f7f;
  font-size: 0.78rem;
  font-weight: 700;
}

.popup-required-mark {
  margin-left: 2px;
  color: #f5365c;
}

.popup-image-upload {
  width: 100%;
  overflow: hidden;
  border: 1px solid #e3e6ed;
  border-radius: 4px;
  background: #f7f8fa;
}

.popup-image-upload::v-deep .dropzone {
  display: flex;
  width: 100%;
  min-height: 0;
  aspect-ratio: 4 / 3;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 0;
  background: transparent;
}

.popup-image-upload::v-deep .dropzone > div:not(.dz-preview) {
  width: 100%;
  height: 100%;
}

.popup-image-upload::v-deep .dropzone img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.popup-image-upload::v-deep .subtitle {
  width: min(240px, 80%);
  line-height: 1.5;
}

.popup-form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px 28px;
}

.popup-form-field {
  min-width: 0;
}

.popup-form-field-wide {
  grid-column: 1 / -1;
}

.popup-form-field::v-deep .form-group,
.popup-form-field::v-deep .el-date-editor,
.popup-form-field::v-deep .el-date-editor.el-input {
  width: 100%;
  margin-bottom: 0;
}

.popup-inline-options {
  display: flex;
  min-height: 40px;
  flex-wrap: wrap;
  align-items: center;
}

.platform-link-label {
  display: flex !important;
  gap: 8px;
  align-items: center;
}

.platform-link-label .badge {
  flex: 0 0 auto;
  white-space: nowrap;
}

.popup-target-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  border-top: 1px solid #e3e6ed;
  border-bottom: 1px solid #e3e6ed;
}

.popup-target-selector {
  min-width: 0;
  padding: 20px 24px;
}

.popup-target-selector + .popup-target-selector {
  border-left: 1px solid #e3e6ed;
}

.popup-target-heading {
  display: flex;
  min-height: 46px;
  gap: 20px;
  align-items: flex-start;
  justify-content: space-between;
}

.popup-target-heading h6 {
  margin: 0 0 4px;
  color: #32325d;
  font-size: 0.88rem;
  font-weight: 700;
}

.popup-target-summary {
  color: #8898aa;
  font-size: 0.76rem;
  font-weight: 600;
}

.popup-target-mode {
  display: flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  align-items: center;
}

.popup-target-select {
  width: 100%;
  margin-top: 14px;
}

.popup-target-select::v-deep .el-input__inner {
  min-height: 42px;
}

.popup-target-selected {
  margin-top: 10px;
  padding: 12px;
  border: 1px solid #e3e6ed;
  border-radius: 4px;
  background: #f7f8fa;
}

.popup-target-selected-heading {
  display: flex;
  margin-bottom: 9px;
  color: #525f7f;
  font-size: 0.74rem;
  font-weight: 700;
  justify-content: space-between;
}

.popup-target-selected-heading span:last-child {
  color: #8898aa;
}

.popup-target-tags {
  display: flex;
  max-height: 112px;
  padding-right: 4px;
  overflow-y: auto;
  flex-wrap: wrap;
  gap: 6px;
}

.popup-target-tags::v-deep .el-tag {
  max-width: 100%;
}

.popup-target-tags::v-deep .el-tag__close {
  flex: 0 0 auto;
}

.popup-target-empty {
  min-height: 28px;
  color: #8898aa;
  font-size: 0.76rem;
  line-height: 28px;
}

.popup-target-all {
  display: flex;
  min-height: 42px;
  margin-top: 14px;
  padding: 0 12px;
  border: 1px solid #e3e6ed;
  border-radius: 4px;
  background: #f7f8fa;
  color: #525f7f;
  font-size: 0.82rem;
  font-weight: 600;
  gap: 8px;
  align-items: center;
}

.popup-target-all i {
  color: #00bf9a;
  font-size: 0.82rem;
}

.popup-form-actions {
  display: flex;
  padding: 4px 0 24px;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

@media (max-width: 991px) {
  .popup-form-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .popup-form-field-wide {
    grid-column: auto;
  }

  .popup-target-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .popup-target-selector + .popup-target-selector {
    border-top: 1px solid #e3e6ed;
    border-left: 0;
  }
}

@media (max-width: 575px) {
  .popup-form-header {
    flex-direction: column;
  }

  .popup-form-actions .btn {
    flex: 1 1 calc(50% - 8px);
  }

  .popup-target-heading {
    flex-direction: column;
    gap: 10px;
  }
}
</style>
